import { useId, useState } from "react";
import * as api from "../../api/admin";
import { getPublicTeamRoster } from "../../api/teamRoster";
import { useAdminData, useAdminQuery, useAdminTask } from "./useAdminData";
import {
  ACCESS_OPTIONS,
  ROLE_OPTIONS,
  ROSTER_FIELDS,
  accessFilter,
  fullName,
  number,
  roleLabel,
  downloadFile,
} from "./adminModel";
import {
  ActionMenu,
  AsyncPicker,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  EntityEditor,
  Field,
  Icon,
  Notice,
  Pagination,
  ResourceContent,
  SearchField,
  SectionHeader,
  Toolbar,
  FilterSelect,
} from "./components/AdminUI";
import styles from "./admin.module.css";

const loadUsers = ({ q, role, status, ...page }) =>
  api.getAdminUsers({
    ...page,
    query: q,
    role: role || undefined,
    is_banned: accessFilter(status),
  });
const loadTeams = ({ q, status, ...page }) =>
  api.getAdminTeams({ ...page, query: q, is_banned: accessFilter(status) });
const loadRoster = ({ q, ...page }) =>
  getPublicTeamRoster({ ...page, query: q });
const loadMembers = ({ teamId }) => api.getAdminTeamMembers(teamId);
const userPickerMeta = (user) =>
  `@${user.login} · ${user.email || roleLabel(user.role)}`;
const casePickerLabel = (item) =>
  `${item.project_code ? `${item.project_code} · ` : ""}${item.title}`;

function PersonCell({ person, onClick }) {
  const name = fullName(person);
  return (
    <div className={styles.personCell}>
      <span className={styles.avatar} aria-hidden="true">
        {name
          .split(" ")
          .slice(0, 2)
          .map((word) => word[0])
          .join("")}
      </span>
      <div className={styles.cellStack}>
        <button
          type="button"
          className={`${styles.cellTitle} ${styles.cellTitleButton}`}
          onClick={onClick}
        >
          {name}
        </button>
        <span className={styles.cellMeta}>
          @{person.login} · № {person.user_id}
        </span>
      </div>
    </div>
  );
}
function AccessBadge({ banned }) {
  return (
    <Badge tone={banned ? "danger" : "success"}>
      {banned ? "Заблокирован" : "Активен"}
    </Badge>
  );
}

function BalanceDialog({ target, entity, operation, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [comment, setComment] = useState("");
  const task = useAdminTask();
  const id = useId();
  const deposit = operation === "deposit";
  const label = entity === "user" ? fullName(target) : target.name;
  const amountNumber = Number(amount);
  const valid = Number.isSafeInteger(amountNumber) && amountNumber > 0;
  const submit = (event) => {
    event.preventDefault();
    if (!valid) {
      task.setError("Укажите положительное целое число.");
      return;
    }
    const handler =
      entity === "user"
        ? deposit
          ? api.depositAdminUser
          : api.deductAdminUser
        : deposit
          ? api.depositAdminTeam
          : api.deductAdminTeam;
    task.run(
      () =>
        handler(
          entity === "user" ? target.user_id : target.team_id,
          amountNumber,
          comment.trim() || null,
        ),
      () => {
        onSaved(
          `${deposit ? "Начислено" : "Списано"} ${number(amountNumber)} ЮФУнтов: ${label}.`,
        );
        onClose();
      },
    );
  };
  return (
    <Dialog
      compact
      title={deposit ? "Начислить ЮФУнты" : "Списать ЮФУнты"}
      description={label}
      busy={task.busy}
      dirty={Boolean(amount || comment)}
      onClose={onClose}
      footer={(requestClose) => (
        <>
          <Notice danger>{task.error}</Notice>
          <div className={styles.footerButtons}>
            <Button onClick={requestClose} disabled={task.busy}>
              Отмена
            </Button>
            <Button
              variant={deposit ? "primary" : "danger"}
              form={id}
              type="submit"
              disabled={task.busy || !valid}
            >
              {task.busy ? "Выполняем…" : deposit ? "Начислить" : "Списать"}
            </Button>
          </div>
        </>
      )}
    >
      <form id={id} className={styles.stack} onSubmit={submit}>
        <div className={styles.balancePreview}>
          <span>Текущий баланс</span>
          <strong>
            {number(target.balance)} <small>ЮФ</small>
          </strong>
        </div>
        <Field
          label="Сумма в ЮФУнтах"
          type="number"
          required
          min="1"
          step="1"
          max={Number.MAX_SAFE_INTEGER}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          disabled={task.busy}
          placeholder="Например, 100"
        />
        <Field
          label="Комментарий к операции"
          type="textarea"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          disabled={task.busy}
          placeholder="За что начисляем или списываем ЮФУнты"
        />
        {valid && (
          <p className={styles.hint}>
            Баланс после операции:{" "}
            <strong>
              {number(
                Number(target.balance) +
                  (deposit ? amountNumber : -amountNumber),
              )}{" "}
              ЮФ
            </strong>
          </p>
        )}
      </form>
    </Dialog>
  );
}
function BanDialog({ target, entity, onSaved, onClose }) {
  return (
    <EntityEditor
      title="Заблокировать доступ"
      description={entity === "user" ? fullName(target) : target.name}
      fields={[
        {
          name: "reason",
          label: "Причина блокировки",
          type: "textarea",
          required: true,
          maxLength: 2000,
          wide: true,
          hint: "Укажите причину, чтобы решение было понятно другим администраторам.",
        },
      ]}
      onClose={onClose}
      onSave={({ reason }) =>
        entity === "user"
          ? api.banAdminUser(target.user_id, reason)
          : api.banAdminTeam(target.team_id, reason)
      }
      onSaved={() => onSaved("Доступ заблокирован.")}
    />
  );
}

export function UsersSection({ notify, refreshDashboard, navigate }) {
  const query = useAdminQuery({ role: "", status: "" });
  const resource = useAdminData(loadUsers, query.values);
  const [dialog, setDialog] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const changed = (message) => {
    notify(message);
    resource.refresh();
    refreshDashboard();
  };
  const edit = (target) => setDialog({ type: "edit", target });
  const askDelete = (user) =>
    setConfirm({
      title: "Удалить пользователя?",
      description: fullName(user),
      confirmLabel: "Удалить пользователя",
      danger: true,
      children: (
        <p className={styles.confirmText}>
          Учётная запись будет удалена без возможности восстановления. Для
          временного ограничения доступа используйте блокировку.
        </p>
      ),
      onConfirm: async () => {
        await api.deleteAdminUser(user.user_id);
        changed("Пользователь удалён.");
      },
    });
  const columns = [
    {
      key: "name",
      label: "Пользователь",
      render: (item) => <PersonCell person={item} onClick={() => edit(item)} />,
    },
    {
      key: "email",
      label: "Контакты",
      className: styles.hideMedium,
      render: (item) => (
        <div className={styles.cellStack}>
          <span className={styles.cellMeta}>{item.email}</span>
          <Badge tone={item.email_verified ? "neutral" : "warning"}>
            {item.email_verified
              ? "Почта подтверждена"
              : "Почта не подтверждена"}
          </Badge>
        </div>
      ),
    },
    {
      key: "role",
      label: "Роль и отбор",
      render: (item) => (
        <div className={styles.cellStack}>
          <span>{roleLabel(item.role)}</span>

        </div>
      ),
    },
    {
      key: "balance",
      label: "Баланс",
      render: (item) => (
        <span className={styles.cellMono}>
          {number(item.balance)} <span className={styles.cellMeta}>ЮФ</span>
        </span>
      ),
    },
    {
      key: "status",
      label: "Доступ",
      render: (item) => <AccessBadge banned={item.is_banned} />,
    },
    {
      key: "actions",
      label: "Действия",
      render: (item) => (
        <div className={styles.rowActions}>
          <Button variant="ghost" onClick={() => edit(item)}>
            Открыть
          </Button>
          <ActionMenu
            label={fullName(item)}
            actions={[
              {
                label: "Начислить ЮФУнты",
                icon: "plus",
                onClick: () =>
                  setDialog({
                    type: "balance",
                    target: item,
                    operation: "deposit",
                  }),
              },
              {
                label: "Списать ЮФУнты",
                icon: "wallet",
                onClick: () =>
                  setDialog({
                    type: "balance",
                    target: item,
                    operation: "deduct",
                  }),
              },
              !item.email_verified && {
                label: "Подтвердить почту",
                icon: "check",
                onClick: () =>
                  setConfirm({
                    title: "Подтвердить почту?",
                    description: item.email,
                    onConfirm: async () => {
                      await api.verifyAdminUserEmail(item.user_id);
                      changed("Почта пользователя подтверждена.");
                    },
                  }),
              },
              {
                label: item.is_banned ? "Разблокировать" : "Заблокировать",
                icon: "shield",
                onClick: () =>
                  item.is_banned
                    ? setConfirm({
                        title: "Восстановить доступ?",
                        description: fullName(item),
                        confirmLabel: "Разблокировать",
                        onConfirm: async () => {
                          await api.unbanAdminUser(item.user_id);
                          changed("Доступ восстановлен.");
                        },
                      })
                    : setDialog({ type: "ban", target: item }),
              },
              {
                label: "Удалить пользователя",
                danger: true,
                onClick: () => askDelete(item),
              },
            ]}
          />
        </div>
      ),
    },
  ];
  const userFields = [
    {
      name: "role",
      label: "Роль на платформе",
      type: "select",
      options: ROLE_OPTIONS.filter(option => ["ADMIN", "MEMBER"].includes(option.value)),
      required: true,
      group: "Доступ и участие",
      hint: "Роли наставника, координатора и эксперта назначаются в разделе «Участники сезона».",
    },

    {
      name: "last_name",
      label: "Фамилия",
      required: true,
      maxLength: 100,
      group: "Профиль",
    },
    { name: "first_name", label: "Имя", required: true, maxLength: 100 },
    { name: "patronymic", label: "Отчество", maxLength: 100 },
    { name: "study_group", label: "Учебная группа", maxLength: 50 },
    { name: "login", label: "Логин", required: true, maxLength: 50 },
    {
      name: "email",
      label: "Электронная почта",
      type: "email",
      required: true,
      maxLength: 100,
    },
  ];
  return (
    <>
      <SectionHeader
        title="Пользователи"
        description="Профили участников, доступ к платформе и статусы отбора."
      >
        <Button icon="upload" onClick={() => navigate("imports")}>
          Импортировать
        </Button>
      </SectionHeader>
      <div className={styles.panel}>
        <Toolbar
          onRefresh={resource.refresh}
          loading={resource.loading}
          onReset={query.reset}
          filtered={query.values.q || query.values.role || query.values.status}
        >
          <SearchField
            placeholder="Имя, почта или логин"
            value={query.values.q}
            onChange={(q) => query.update({ q })}
          />
          <FilterSelect
            label="Роль пользователя"
            value={query.values.role}
            onChange={(role) => query.update({ role })}
            options={[{ value: "", label: "Все роли" }, ...ROLE_OPTIONS]}
          />
          <FilterSelect
            label="Доступ пользователя"
            value={query.values.status}
            onChange={(status) => query.update({ status })}
            options={ACCESS_OPTIONS}
          />
        </Toolbar>
        <ResourceContent resource={resource}>
          <DataTable
            caption="Пользователи платформы"
            columns={columns}
            items={resource.items}
            rowKey="user_id"
          />
        </ResourceContent>
        <Pagination query={query} resource={resource} />
      </div>
      {dialog?.type === "edit" && (
        <EntityEditor
          title="Профиль пользователя"
          description={fullName(dialog.target)}
          fields={userFields}
          item={{
            ...dialog.target,
            role: dialog.target.role === "ADMIN" ? "ADMIN" : "MEMBER",
          }}
          onSave={(payload) =>
            api.updateAdminUser(dialog.target.user_id, payload)
          }
          onSaved={() => changed("Профиль пользователя сохранён.")}
          onClose={() => setDialog(null)}
        >
          <div className={styles.detailSummary}>
            <div className={styles.detailFacts}>
              <div>
                <small>Баланс</small>
                <strong>{number(dialog.target.balance)} ЮФ</strong>
              </div>
              <div>
                <small>Доступ</small>
                <AccessBadge banned={dialog.target.is_banned} />
              </div>
              <div>
                <small>Почта</small>
                <Badge
                  tone={dialog.target.email_verified ? "success" : "warning"}
                >
                  {dialog.target.email_verified
                    ? "Подтверждена"
                    : "Не подтверждена"}
                </Badge>
              </div>
            </div>
          </div>
        </EntityEditor>
      )}
      {dialog?.type === "balance" && (
        <BalanceDialog
          {...dialog}
          entity="user"
          onSaved={changed}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === "ban" && (
        <BanDialog
          {...dialog}
          entity="user"
          onSaved={changed}
          onClose={() => setDialog(null)}
        />
      )}
      {confirm && (
        <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />
      )}
    </>
  );
}

function TeamEditor({ team, onClose, onSaved }) {
  const [name, setName] = useState(team.name);
  const [chat, setChat] = useState(team.tracker_chat_url || "");
  const [selectedCase, setSelectedCase] = useState(
    team.showcase_case_id
      ? {
          case_id: team.showcase_case_id,
          title: team.showcase_case_title || `Кейс № ${team.showcase_case_id}`,
        }
      : null,
  );
  const task = useAdminTask();
  const id = useId();
  const dirty =
    name !== team.name ||
    chat !== (team.tracker_chat_url || "") ||
    (selectedCase?.case_id || null) !== (team.showcase_case_id || null);
  return (
    <Dialog
      title="Настройки команды"
      description={team.name}
      onClose={onClose}
      dirty={dirty}
      busy={task.busy}
      footer={(requestClose) => (
        <>
          <Notice danger>{task.error}</Notice>
          <div className={styles.footerButtons}>
            <Button disabled={task.busy} onClick={requestClose}>
              Отмена
            </Button>
            <Button
              variant="primary"
              type="submit"
              form={id}
              disabled={task.busy}
            >
              {task.busy ? "Сохраняем…" : "Сохранить"}
            </Button>
          </div>
        </>
      )}
    >
      <form
        id={id}
        className={styles.stack}
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim().length < 3) {
            task.setError("Название должно содержать не менее 3 символов.");
            return;
          }
          task.run(
            () =>
              api.updateAdminTeam(team.team_id, {
                name: name.trim(),
                showcase_case_id: selectedCase?.case_id || null,
                tracker_chat_url: chat.trim() || null,
              }),
            () => {
              onSaved("Команда обновлена.");
              onClose();
            },
          );
        }}
      >
        <Field
          label="Название команды"
          required
          minLength={3}
          maxLength={100}
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={task.busy}
        />
        <AsyncPicker
          label="Кейс витрины"
          loader={api.getAdminShowcaseCases}
          value={selectedCase}
          onChange={setSelectedCase}
          idKey="case_id"
          getLabel={casePickerLabel}
          getMeta={(item) => item.partner_name}
          disabled={task.busy}
        />
        {selectedCase && (
          <button
            className={styles.textButton}
            type="button"
            disabled={task.busy}
            onClick={() => setSelectedCase(null)}
          >
            Отвязать кейс от команды
          </button>
        )}
        <Field
          label="Ссылка на чат Трекера"
          type="url"
          maxLength={1000}
          value={chat}
          onChange={(event) => setChat(event.target.value)}
          placeholder="https://…"
          disabled={task.busy}
        />
      </form>
    </Dialog>
  );
}

function TeamMembers({ team, onClose, onSaved }) {
  const resource = useAdminData(loadMembers, { teamId: team.team_id });
  const task = useAdminTask();
  const [person, setPerson] = useState(null);
  const [role, setRole] = useState("");
  const [pending, setPending] = useState(null);
  const success = (message) => {
    onSaved(message);
    resource.refresh();
  };
  return (
    <Dialog
      title="Состав команды"
      description={team.name}
      busy={task.busy}
      dirty={Boolean(person || role)}
      onClose={onClose}
      footer={(requestClose) => (
        <>
          <Notice danger>{task.error}</Notice>
          <div className={styles.footerButtons}>
            <Button onClick={requestClose} disabled={task.busy}>
              Готово
            </Button>
          </div>
        </>
      )}
    >
      <div className={styles.stack}>
        <h3 className={styles.subheading}>Добавить участника</h3>
        <AsyncPicker
          label="Пользователь"
          loader={api.getAdminUsers}
          value={person}
          onChange={setPerson}
          getLabel={fullName}
          getMeta={userPickerMeta}
          idKey="user_id"
          disabled={task.busy}
        />
        <Field
          label="Роль в команде"
          hint="Необязательно: например, дизайнер или разработчик."
          value={role}
          onChange={(event) => setRole(event.target.value)}
          disabled={task.busy}
        />
        <div>
          <Button
            icon="plus"
            variant="primary"
            disabled={!person || task.busy}
            onClick={() =>
              task.run(
                () =>
                  api.addAdminTeamMember(
                    team.team_id,
                    person.user_id,
                    role.trim() || null,
                  ),
                () => {
                  setPerson(null);
                  setRole("");
                  success("Участник добавлен в команду.");
                },
              )
            }
          >
            Добавить в команду
          </Button>
        </div>
      </div>
      <div className={styles.divider} />
      <h3 className={styles.subheading}>
        Участники {!resource.loading && `· ${resource.items.length}`}
      </h3>
      {pending && (
        <div className={styles.detailSummary}>
          <p className={styles.confirmText}>
            {pending.type === "captain"
              ? `Назначить ${fullName(pending.member)} капитаном? Текущий капитан станет участником.`
              : `Убрать ${fullName(pending.member)} из команды?`}
          </p>
          <div className={styles.inlineActions}>
            <Button disabled={task.busy} onClick={() => setPending(null)}>
              Отмена
            </Button>
            <Button
              variant={pending.type === "captain" ? "primary" : "danger"}
              disabled={task.busy}
              onClick={() =>
                task.run(
                  () =>
                    pending.type === "captain"
                      ? api.setAdminTeamCaptain(
                          team.team_id,
                          pending.member.user_id,
                        )
                      : api.removeAdminTeamMember(
                          team.team_id,
                          pending.member.user_id,
                        ),
                  () => {
                    setPending(null);
                    success("Состав команды обновлён.");
                  },
                )
              }
            >
              {task.busy ? "Сохраняем…" : "Подтвердить"}
            </Button>
          </div>
        </div>
      )}
      <ResourceContent
        resource={resource}
        emptyTitle="В команде пока никого нет"
        emptyDescription="Найдите пользователя выше и добавьте его в состав."
      >
        <div className={styles.memberList}>
          {resource.items.map((member) => (
            <div key={member.user_id} className={styles.memberRow}>
              <div className={styles.cellStack}>
                <strong>{fullName(member)}</strong>
                <span className={styles.cellMeta}>
                  @{member.login}
                  {member.custom_role ? ` · ${member.custom_role}` : ""}
                </span>
              </div>
              <div className={styles.inlineActions}>
                {member.system_role === "CAPTAIN" ? (
                  <Badge tone="primary">Капитан</Badge>
                ) : (
                  <>
                    <Button
                      disabled={task.busy}
                      onClick={() => setPending({ type: "captain", member })}
                    >
                      Назначить капитаном
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={task.busy}
                      onClick={() => setPending({ type: "remove", member })}
                    >
                      Убрать из команды
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </ResourceContent>
    </Dialog>
  );
}

export function TeamsSection({ notify, refreshDashboard, navigate }) {
  const query = useAdminQuery({ status: "" });
  const resource = useAdminData(loadTeams, query.values);
  const [dialog, setDialog] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const task = useAdminTask();
  const changed = (message) => {
    notify(message);
    resource.refresh();
    refreshDashboard();
  };
  const columns = [
    {
      key: "name",
      label: "Команда",
      render: (item) => (
        <div className={styles.cellStack}>
          <button
            type="button"
            className={`${styles.cellTitle} ${styles.cellTitleButton}`}
            onClick={() => setDialog({ type: "edit", target: item })}
          >
            {item.name}
          </button>
          <span className={styles.cellMeta}>
            {item.project_code || `№ ${item.team_id}`}
            {item.captain_login ? ` · Капитан @${item.captain_login}` : ""}
          </span>
        </div>
      ),
    },
    {
      key: "case",
      label: "Проект",
      className: styles.hideMedium,
      render: (item) => (
        <span className={styles.cellDescription}>
          {item.showcase_case_title || "Кейс не назначен"}
        </span>
      ),
    },
    {
      key: "members",
      label: "Участники",
      render: (item) => (
        <button
          type="button"
          className={styles.textButton}
          onClick={() => setDialog({ type: "members", target: item })}
        >
          {number(item.member_count)} чел.
        </button>
      ),
    },
    {
      key: "balance",
      label: "Баланс",
      render: (item) => (
        <span className={styles.cellMono}>
          {number(item.balance)} <span className={styles.cellMeta}>ЮФ</span>
        </span>
      ),
    },
    {
      key: "status",
      label: "Доступ",
      render: (item) => <AccessBadge banned={item.is_banned} />,
    },
    {
      key: "actions",
      label: "Действия",
      render: (item) => (
        <div className={styles.rowActions}>
          <Button
            variant="ghost"
            onClick={() => setDialog({ type: "members", target: item })}
          >
            Состав
          </Button>
          <ActionMenu
            label={item.name}
            actions={[
              {
                label: "Настройки команды",
                icon: "edit",
                onClick: () => setDialog({ type: "edit", target: item }),
              },
              {
                label: "Начислить ЮФУнты",
                icon: "plus",
                onClick: () =>
                  setDialog({
                    type: "balance",
                    target: item,
                    operation: "deposit",
                  }),
              },
              {
                label: "Списать ЮФУнты",
                icon: "wallet",
                onClick: () =>
                  setDialog({
                    type: "balance",
                    target: item,
                    operation: "deduct",
                  }),
              },
              {
                label: item.is_banned ? "Разблокировать" : "Заблокировать",
                icon: "shield",
                onClick: () =>
                  item.is_banned
                    ? setConfirm({
                        title: "Разблокировать команду?",
                        description: item.name,
                        onConfirm: async () => {
                          await api.unbanAdminTeam(item.team_id);
                          changed("Команда разблокирована.");
                        },
                      })
                    : setDialog({ type: "ban", target: item }),
              },
              {
                label: "Удалить команду",
                danger: true,
                onClick: () =>
                  setConfirm({
                    title: "Удалить команду?",
                    description: item.name,
                    confirmLabel: "Удалить команду",
                    danger: true,
                    children: (
                      <p className={styles.confirmText}>
                        Это действие нельзя отменить. Учётные записи участников
                        сохранятся.
                      </p>
                    ),
                    onConfirm: async () => {
                      await api.deleteAdminTeam(item.team_id);
                      changed("Команда удалена.");
                    },
                  }),
              },
            ]}
          />
        </div>
      ),
    },
  ];
  return (
    <>
      <SectionHeader
        title="Команды"
        description="Составы, капитаны, проектные задачи и командные балансы."
      >
        <Button
          icon="download"
          disabled={task.busy}
          onClick={() =>
            task.run(api.exportAdminTeamMembers, (blob) => {
              downloadFile(blob, "sostavy-komand.xlsx");
              notify("Составы команд выгружены.");
            })
          }
        >
          {task.busy ? "Выгружаем…" : "Экспорт"}
        </Button>
        <Button icon="upload" onClick={() => navigate("imports")}>
          Импортировать
        </Button>
      </SectionHeader>
      {task.error && (
        <Notice danger onClose={() => task.setError("")}>
          {task.error}
        </Notice>
      )}
      <div className={styles.panel}>
        <Toolbar
          onRefresh={resource.refresh}
          loading={resource.loading}
          onReset={query.reset}
          filtered={query.values.q || query.values.status}
        >
          <SearchField
            placeholder="Найти команду по названию"
            value={query.values.q}
            onChange={(q) => query.update({ q })}
          />
          <FilterSelect
            label="Доступ команды"
            value={query.values.status}
            onChange={(status) => query.update({ status })}
            options={ACCESS_OPTIONS}
          />
        </Toolbar>
        <ResourceContent resource={resource}>
          <DataTable
            caption="Команды платформы"
            columns={columns}
            items={resource.items}
            rowKey="team_id"
          />
        </ResourceContent>
        <Pagination query={query} resource={resource} />
      </div>
      {dialog?.type === "edit" && (
        <TeamEditor
          team={dialog.target}
          onClose={() => setDialog(null)}
          onSaved={changed}
        />
      )}
      {dialog?.type === "members" && (
        <TeamMembers
          team={dialog.target}
          onClose={() => setDialog(null)}
          onSaved={changed}
        />
      )}
      {dialog?.type === "balance" && (
        <BalanceDialog
          {...dialog}
          entity="team"
          onClose={() => setDialog(null)}
          onSaved={changed}
        />
      )}
      {dialog?.type === "ban" && (
        <BanDialog
          {...dialog}
          entity="team"
          onClose={() => setDialog(null)}
          onSaved={changed}
        />
      )}
      {confirm && (
        <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />
      )}
    </>
  );
}

export function RosterSection({ notify, navigate }) {
  const query = useAdminQuery();
  const resource = useAdminData(loadRoster, query.values);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const changed = (message) => {
    notify(message);
    resource.refresh();
  };
  const columns = [
    {
      key: "topic",
      label: "Проект",
      render: (item) => (
        <div className={styles.cellStack}>
          <span className={styles.cellMeta}>{item.project_code}</span>
          <button
            type="button"
            className={`${styles.cellTitle} ${styles.cellTitleButton}`}
            onClick={() => setEdit(item)}
          >
            {item.topic || "Без темы"}
          </button>
        </div>
      ),
    },
    {
      key: "track",
      label: "Трек",
      render: (item) => <Badge>{item.track_label || "Не указан"}</Badge>,
    },
    {
      key: "mentor",
      label: "Наставник",
      render: (item) => (
        <span className={styles.cellDescription}>
          {item.mentor_name || "Не назначен"}
        </span>
      ),
    },
    {
      key: "members",
      label: "Участники",
      render: (item) =>
        `${item.member_count ?? item.members?.length ?? 0} чел.`,
    },
    {
      key: "actions",
      label: "Действия",
      render: (item) => (
        <div className={styles.rowActions}>
          <Button variant="ghost" onClick={() => setEdit(item)}>
            Редактировать
          </Button>
          <ActionMenu
            label={item.project_code}
            actions={[
              {
                label: "Удалить карточку",
                danger: true,
                onClick: () =>
                  setConfirm({
                    title: "Удалить публичную карточку?",
                    description: item.project_code,
                    confirmLabel: "Удалить карточку",
                    danger: true,
                    children: (
                      <p className={styles.confirmText}>
                        Карточка исчезнет из публичного списка. Команда и
                        участники в личных кабинетах сохранятся.
                      </p>
                    ),
                    onConfirm: async () => {
                      await api.deleteAdminTeamRosterEntry(item.entry_id);
                      changed("Публичная карточка удалена.");
                    },
                  }),
              },
            ]}
          />
        </div>
      ),
    },
  ];
  return (
    <>
      <SectionHeader
        title="Публичный список команд"
        description="Карточки проектов и составы для страницы участников интенсива."
      >
        <Button icon="upload" onClick={() => navigate("imports")}>
          Импортировать
        </Button>
        <ActionMenu
          label="Публичный список"
          actions={[
            {
              label: "Очистить весь список",
              danger: true,
              onClick: () =>
                setConfirm({
                  title: "Очистить публичный список?",
                  description:
                    "Будут удалены все публичные карточки, включая скрытые текущим фильтром.",
                  danger: true,
                  confirmLabel: "Удалить все карточки",
                  requiredText: "УДАЛИТЬ",
                  onConfirm: async () => {
                    await api.clearAdminTeamRoster();
                    changed("Публичный список очищен.");
                  },
                }),
            },
          ]}
        />
      </SectionHeader>
      <div className={styles.helpBanner}>
        <Icon name="globe" />
        <span>
          Публичные карточки редактируются отдельно от команд в личных
          кабинетах. Изменение списка участников здесь не меняет членство на
          платформе.
        </span>
      </div>
      <div className={styles.panel}>
        <Toolbar
          onRefresh={resource.refresh}
          loading={resource.loading}
          onReset={query.reset}
          filtered={query.values.q}
        >
          <SearchField
            placeholder="Код проекта, тема или наставник"
            value={query.values.q}
            onChange={(q) => query.update({ q })}
          />
        </Toolbar>
        <ResourceContent resource={resource}>
          <DataTable
            caption="Публичные карточки команд"
            columns={columns}
            items={resource.items}
            rowKey="entry_id"
          />
        </ResourceContent>
        <Pagination query={query} resource={resource} />
      </div>
      {edit && (
        <EntityEditor
          title="Публичная карточка команды"
          description={edit.project_code}
          fields={ROSTER_FIELDS}
          item={{ ...edit, members_text: (edit.members || []).join("\n") }}
          onClose={() => setEdit(null)}
          onSave={({ members_text, ...payload }) =>
            api.updateAdminTeamRosterEntry(edit.entry_id, {
              ...payload,
              members: (members_text || "")
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean),
            })
          }
          onSaved={() => changed("Публичная карточка сохранена.")}
        />
      )}
      {confirm && (
        <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />
      )}
    </>
  );
}
